/**
 * Privacy settings — the permanent place to see and change the choice.
 *
 * Reachable from the banner ("Choose settings") and forever afterwards from
 * the footer link. Essential is shown and explained but cannot be switched
 * off; Analytics and Advertising are independent and off unless allowed.
 */
import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  CATEGORY_COPY,
  OPTIONAL_CATEGORIES,
  PRIVACY_ACTIONS,
  PRIVACY_BANNER_BODY,
  PRIVACY_SETTINGS_LABEL,
  type OptionalCategory,
} from "@/lib/consent/categories";
import {
  acceptAll,
  getConsentState,
  onConsentStateChange,
  rejectOptional,
  setConsentState,
  type PrivacyConsentState,
} from "@/lib/consent/store";
import { logCategoryChoice } from "@/lib/consent/log";

/** Footer entry-point. Always available, for everyone. */
export function PrivacySettingsLink({ className }: { className?: string }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          className={
            className ??
            "text-left text-sm text-muted-foreground transition-colors hover:text-brand"
          }
        >
          {PRIVACY_SETTINGS_LABEL}
        </button>
      </DialogTrigger>
      <SettingsBody />
    </Dialog>
  );
}

export function PrivacySettingsDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <SettingsBody onDone={() => onOpenChange(false)} />
    </Dialog>
  );
}

function SettingsBody({ onDone }: { onDone?: () => void }) {
  const [state, setState] = useState<PrivacyConsentState>(() => getConsentState());

  useEffect(() => {
    setState(getConsentState());
    return onConsentStateChange(setState);
  }, []);

  const apply = (next: PrivacyConsentState) => {
    setState(next);
    void logCategoryChoice(next);
  };

  const toggle = (category: OptionalCategory, on: boolean) =>
    apply(setConsentState({ [category]: on }, "privacy_settings"));

  return (
    <DialogContent className="max-h-[85dvh] overflow-y-auto p-4 sm:max-w-lg sm:p-6">
      <DialogHeader>
        <DialogTitle className="text-base sm:text-lg">{PRIVACY_SETTINGS_LABEL}</DialogTitle>
        <DialogDescription className="text-xs leading-relaxed sm:text-sm">{PRIVACY_BANNER_BODY}</DialogDescription>
      </DialogHeader>

      <p className="text-xs text-muted-foreground">If tracking is already active, withdrawing a category refreshes this page to stop loaded tools. Save unfinished form answers first.</p>
      <div className="space-y-2 sm:space-y-3">
        <CategoryRow
          label={CATEGORY_COPY.essential.label}
          description={CATEGORY_COPY.essential.description}
          checked
          locked
        />
        {OPTIONAL_CATEGORIES.map((category) => (
          <CategoryRow
            key={category}
            label={CATEGORY_COPY[category].label}
            description={CATEGORY_COPY[category].description}
            checked={state[category] === "granted"}
            onChange={(on) => toggle(category, on)}
          />
        ))}
      </div>

      <p className="text-xs leading-relaxed text-muted-foreground">
        Read our{" "}
        <Link to="/policies/privacy-policy" className="text-brand underline-offset-4 hover:underline">
          privacy notice
        </Link>{" "}
        or write to{" "}
        <a href="mailto:privacy@truwe.in" className="text-brand underline-offset-4 hover:underline">
          privacy@truwe.in
        </a>{" "}
        for any privacy request.
        {state.updatedAt ? (
          <>
            {" "}
            Last updated {new Date(state.updatedAt).toLocaleString()}.
          </>
        ) : null}
      </p>

      <DialogFooter className="gap-2 sm:justify-between">
        <Button
          variant="outline"
          onClick={() => {
            apply(rejectOptional("privacy_settings"));
            onDone?.();
          }}
        >
          {PRIVACY_ACTIONS.rejectOptional}
        </Button>
        <Button
          onClick={() => {
            apply(acceptAll("privacy_settings"));
            onDone?.();
          }}
        >
          {PRIVACY_ACTIONS.acceptAll}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function CategoryRow({
  label,
  description,
  checked,
  locked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  locked?: boolean;
  onChange?: (on: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-xl border border-border bg-muted/30 p-3 sm:p-4">
      <div className="min-w-0">
        <p className="text-xs font-semibold text-foreground sm:text-sm">
          {label}
          {locked ? (
            <span className="ml-2 text-[11px] font-normal text-muted-foreground sm:text-xs">Always on</span>
          ) : null}
        </p>
        <p className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground sm:mt-1 sm:text-xs">{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={locked}
        onClick={() => onChange?.(!checked)}
        className={`relative mt-0.5 h-5 w-9 shrink-0 rounded-full transition-colors sm:h-6 sm:w-11 ${
          checked ? "bg-brand" : "bg-muted-foreground/30"
        } ${locked ? "cursor-not-allowed opacity-60" : ""}`}
      >
        <span
          className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all sm:h-5 sm:w-5 ${
            checked ? "left-[1.125rem] sm:left-[1.375rem]" : "left-0.5"
          }`}
        />
      </button>
    </div>
  );
}
